import {
  IInventoryPurchaseOrderDraftInvoiceDetails,
  IInventoryPurchaseOrderDraftInvoiceDetailsAttach,
  IInventoryPurchaseOrderDraftProductInfo,
  IInventoryPurchaseOrderDraftPropsBody,
  POStatusTypes,
} from "@/api_framework/api_modals/Inventory";
import { IPOStatusUpdateModalState } from "@/app/components/routes/modals/POStatusUpdate";
import SimpleBadge from "@/app/components/ui/Badges/SimpleBadge";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import { usePurchaseOrderDetailsAPI } from "@/app/hooks/api_hooks/Inventory/PO/usePurchaseOrderDetailsAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useMappedConstants } from "@/app/hooks/useMappedConstants";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { IInventoryState, setActivePOId } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { isObject, toNumber } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import AdditionalNotesFormFields from "./AddtionalNotesFormFields";
import ManageInvoiceFormFields from "./ManageInvoiceFormFields";
import POPDFPreview from "./POPDFPreiview";
import POSubmitButton from "./POSubmitButton";
import PrimaryPurchaseInfoFormFields from "./PrimaryPurchaseInfoFormFields";
import ProductInfoFormFields, { IOcrVerificationGate } from "./ProductInfoFormFields";
import { useInventoryPurchaseOrderUpdateAPI } from "@/app/hooks/api_hooks/Inventory/PO/useInventoryPurchaseOrderUpdateAPI";
import { usePurchaseOrderDetailsByPoNumberAPI} from "@/app/hooks/api_hooks/Inventory/PO/usePurchaseOrderDetailsByPoNumberAPI";
import toast from "react-hot-toast";

export interface IInventoryPurchaseOrderFormFields extends IInventoryPurchaseOrderDraftPropsBody {
  attachments: IInventoryPurchaseOrderDraftInvoiceDetailsAttach[];
  invoice_attachment_file?: FileList;
}

export interface VendorSectionSearchResultType {
  name: string;
  vendor_id: string;
}

const OrderPurchaseAddComponent = () => {
  const { getRouteKey } = useRouter();
  const [pdfView, setPDFView] = useState<boolean>(false);
  // Filled by the product table while the PO is in invoice verification; read by "Open For GRN".
  const ocrGateRef = useRef<IOcrVerificationGate | null>(null);

  const productCalculationRef = useRef<Promise<unknown> | null>(null);
  const [previousPONumber, setPreviousPONumber] = useState<string>("");
  const [isCopyingPreviousPO, setIsCopyingPreviousPO] = useState<boolean>(false);
  const [copyPreviousPOError, setCopyPreviousPOError] = useState<string>("");
  const { setVisible: showPOStatusUpdateModal, setModalData: setPOStatusUpdateModalData } =
    useModalActions("PO_STATUS_UPDATE");
  const { setVisible: openCopyPreviousPOModal, setInvisible: hideCopyPreviousPOModal } =
    useModalActions("PO_COPY_PREVIOUS_MODAL");
  const [nextPOStatus, setNextPOStatus] = useState<POStatusTypes>("pending_invoice_upload");
  
  const [isInvoiceUploading, setIsInvoiceUploading] = useState<boolean>(false);
  const { POStatusBadgeColor } = useMappedConstants();
  const [params, _setparams] = useSearchParams();
  const { fileListToBase64, convertToReadableFormat} = useUtils();
  const dispatch = useAppDispatch();
  const updatePOWithNextStatus = useInventoryPurchaseOrderUpdateAPI();
  const { execute: fetchPOdetails } = usePurchaseOrderDetailsAPI();
  const { execute: fetchPOdetailsByNumber } = usePurchaseOrderDetailsByPoNumberAPI();
  const { purchaseOrderDetails } = useRouteInfo(getRouteKey("INVENTORY", "id"))?.routeState.state as IInventoryState;

  const formHook = useForm<IInventoryPurchaseOrderFormFields>({
    mode: "onChange",
    defaultValues: {
      attachments: [],
      product_info: [],
      primary_purchase_info: {
        delivery_date: null,
      },
      invoice_details: {
        invoice_value: null,
      },
    },
  });

  const attachementFieldHook = useFieldArray<IInventoryPurchaseOrderFormFields>({
    name: "attachments",
    control: formHook.control,
  });

  const POProductsHook = useFieldArray({
    name: "product_info",
    control: formHook.control,
  });

  const SynthesisePOProductsPayload = (productsInfo: IInventoryPurchaseOrderDraftProductInfo[]) => {
    if (!productsInfo) return [];
    return productsInfo.map((np) => {
      return {
        ...np,
        package_count: toNumber(np.package_count),
        package_unit: np.package_unit,
        price_per_package: toNumber(np.price_per_package),
        discount_percentage: np.discount_percentage === 0 ? 0 : toNumber(np.discount_percentage),
        discount_type: np.discount_type,
        total_cost_before_tax: np?.total_cost_before_tax || 0,
        hsn_code: np?.hsn_code || null,
        total_discount_before_tax: np?.total_discount_before_tax || 0,
        total_cost_after_tax: np?.total_cost_after_tax || 0,
      };
    });
  };

  const constructPOUpdatePayload = (
    formData: IInventoryPurchaseOrderFormFields,
    POStatusNext: POStatusTypes,
  ): IInventoryPurchaseOrderDraftPropsBody => {
    const isInvoiceVerificationSave =
      purchaseOrderDetails?.po_status === "pending_invoice_verification" &&
      POStatusNext === "pending_invoice_verification" &&
      Boolean(formData?.invoice_details?.ocr);
    const verificationMarkers = isInvoiceVerificationSave ? ocrGateRef.current?.getSaveMarkers() : undefined;
    const isRejectedLine = (line?: IInventoryPurchaseOrderDraftProductInfo) => line?.ocr_decision === "rejected";
    const acceptedVariantIds = new Set(
      verificationMarkers?.acceptedVariantIds ??
        (isInvoiceVerificationSave
          ? (formData?.product_info ?? []).filter((line) => line?.ocr_decision === "accepted").map((line) => line.variant_id)
          : []),
    );
    // Rejected lines are kept on the PO only while it stays in verification; any other save drops them.
    const rejectedLines = isInvoiceVerificationSave
      ? (verificationMarkers?.rejectedLines ?? (purchaseOrderDetails?.product_info ?? []).filter(isRejectedLine))
      : [];
    const data: IInventoryPurchaseOrderFormFields = {
      ...formData,
      ocr_applied: isInvoiceVerificationSave,
      product_info: formData?.product_info && [
        ...formData.product_info
          .filter((line) => !isRejectedLine(line))
          .map(({ ocr_decision: _ocrDecision, ...line }) =>
            acceptedVariantIds.has(line?.variant_id) ? { ...line, ocr_decision: "accepted" as const } : line,
          ),
        ...rejectedLines.map((line) => ({ ...line, ocr_decision: "rejected" as const })),
      ],
    };
    const productsInfoCleanPayload = SynthesisePOProductsPayload(data.product_info);
    const { attachments, invoice_attachment_file: _invoiceAttachmentFile, ...payloadWithoutAttachmentFile } = data;
    const { ocr: _ocrAnalysis, ...invoiceDetailsWithoutOcr } = payloadWithoutAttachmentFile?.invoice_details ?? {};
    const invoiceDetailsWithAttachments = payloadWithoutAttachmentFile?.invoice_details
      ? ({
          ...invoiceDetailsWithoutOcr,
          attachments: attachments ?? payloadWithoutAttachmentFile.invoice_details?.attachments ?? [],
          // The invoice scan writes this field as a string ("5498.0") and the form can hold null, but
          // the server does a bare float() on it. Always send a number.
          invoice_value: Number(payloadWithoutAttachmentFile.invoice_details?.invoice_value) || 0,
        } as IInventoryPurchaseOrderDraftInvoiceDetails)
      : null
      ;

    if (POStatusNext === "closed") {
      return {
        ...payloadWithoutAttachmentFile,
        invoice_details: invoiceDetailsWithAttachments,
        raised_error_reason: data.raised_error_reason ?? "",
      };
    }
    if (POStatusNext === "issue_in_invoice_upload") {
      return {
        ...payloadWithoutAttachmentFile,
        invoice_details: invoiceDetailsWithAttachments,
        raised_error_reason: data.raised_error_reason,
      };
    }
    if (POStatusNext === "sent_to_finance") {
      return {
        ...payloadWithoutAttachmentFile,
        invoice_details: invoiceDetailsWithAttachments,
        pending_finance_approval_note: data?.pending_finance_approval_note || "",
        credit_note_attachment: data?.credit_note_attachment || "",
      };
    }
    if (POStatusNext === "pending_invoice_upload") {
      return {
        ...payloadWithoutAttachmentFile,
        invoice_details: invoiceDetailsWithAttachments,
      };
    }
    if (POStatusNext === "pending_invoice_verification") {
      const invoiceDetails: IInventoryPurchaseOrderDraftInvoiceDetails = {
        attachments: attachments ?? [],
        invoice_number: data.invoice_details.invoice_number,
        recorded_date: data.primary_purchase_info.delivery_date,
        invoice_date: data?.invoice_details?.invoice_date,
        goods_receiving_date: data?.invoice_details?.goods_receiving_date,
        invoice_value: Number(data?.invoice_details?.invoice_value) || 0,
      };
      if (isInvoiceVerificationSave) {
        // The backend's invoice schema wants numbers; the line inputs hold strings while being typed.
        return {
          ...payloadWithoutAttachmentFile,
          product_info: productsInfoCleanPayload,
          invoice_details: invoiceDetails,
          ...(verificationMarkers && { rejected_ocr_item_indexes: verificationMarkers.rejectedOcrItemIndexes }),
        };
      }
      return {
        ...payloadWithoutAttachmentFile,
        invoice_details: invoiceDetails,
      };
    }
    return {
      ...payloadWithoutAttachmentFile,
      product_info: productsInfoCleanPayload,
      invoice_details: invoiceDetailsWithAttachments,
    };
  };



  const onSubmit = (overrideNextPOStatus?: POStatusTypes) => {
    if (isInvoiceUploading) {
      toast.error("The invoice is still uploading. Please wait for it to finish, then submit.");
      return;
    }
    Promise.resolve(productCalculationRef.current)
      .catch(() => undefined)
      .then(() => formHook.trigger())
      .then((isValid) => {
        if (isValid) {
          const nPOStatus = overrideNextPOStatus ? overrideNextPOStatus : nextPOStatus;
          const payload = constructPOUpdatePayload(formHook.getValues(), nPOStatus);
          setPOStatusUpdateModalData({
            nextPOStatus: nPOStatus,
            prevPOStatus: purchaseOrderDetails?.po_status,
            POPayload: { ...payload },
          } as IPOStatusUpdateModalState);
          showPOStatusUpdateModal();
        }
      });
  };

  const handlePOupdate = (overrideNextPOStatus?: POStatusTypes) => {
    Promise.resolve(productCalculationRef.current)
      .catch(() => undefined)
      .then(() => {
        const payload = constructPOUpdatePayload(formHook.getValues(), overrideNextPOStatus);
        updatePOWithNextStatus(payload, payload.po_id, overrideNextPOStatus);
      });
  };

  /** Background save of invoice verification work, so a refresh or another user sees the same PO. */
  const saveInvoiceVerification = () => {
    if (purchaseOrderDetails?.po_status !== "pending_invoice_verification") return;
    const payload = constructPOUpdatePayload(formHook.getValues(), "pending_invoice_verification");
    return updatePOWithNextStatus(payload, payload.po_id, "pending_invoice_verification", false, { silent: true });
  };
  // await updatePOWithNextStatus(POPayload, POPayload.po_id, nextPOStatus);

  const autoDraftPO = () => {
    const payload = constructPOUpdatePayload(formHook.getValues(), "draft");
    updatePOWithNextStatus(payload, purchaseOrderDetails?.po_id, "draft");
  };

  useEffect(() => {
    if (purchaseOrderDetails?.po_status === "draft" || purchaseOrderDetails?.po_status === "pre_draft") {
      const interval = setInterval(
        () => {
          autoDraftPO();
        },
        5 * 60 * 1000,
      );
      return () => clearInterval(interval);
    }
  }, [purchaseOrderDetails?.po_status]);

  const setAttachments = async () => {
    const response = await fileListToBase64(formHook.getValues("invoice_attachment_file")).then((data) => data);
    formHook.setValue("attachments", response);
  };

  const setCorrectNextPOStatus = (currentPOStatus: POStatusTypes) => {
    if (currentPOStatus === "draft" || currentPOStatus === "pre_draft") {
      setNextPOStatus("pending_invoice_upload");
    }
    if (currentPOStatus === "pending_invoice_upload") {
      setNextPOStatus("pending_invoice_verification");
    }
    if (currentPOStatus === "issue_in_invoice_upload") {
      setNextPOStatus("pending_invoice_verification");
    }
    if (currentPOStatus === "pending_invoice_verification") {
      setNextPOStatus("awaiting_grn");
    }
    if (currentPOStatus === "closed") {
      setNextPOStatus("sent_to_finance");
    }
  };

  const closeCopyPreviousPOModal = () => {
    if (isCopyingPreviousPO) return;
    hideCopyPreviousPOModal();
    setPreviousPONumber("");
    setCopyPreviousPOError("");
  };

  const applyPreviousPODetailsToCurrentForm = (previousPOData: IInventoryPurchaseOrderDraftPropsBody) => {
    const existingInvoiceDetails = formHook.getValues("invoice_details");
    const existingAttachments = formHook.getValues("attachments");
    const existingInvoiceAttachmentFile = formHook.getValues("invoice_attachment_file");

    formHook.setValue("primary_purchase_info.type", previousPOData?.primary_purchase_info?.type || "");
    formHook.setValue(
      "primary_purchase_info.vendor_details.vendor_name",
      previousPOData?.primary_purchase_info?.vendor_details?.vendor_name || "",
    );
    formHook.setValue(
      "primary_purchase_info.vendor_details.vendor_id",
      previousPOData?.primary_purchase_info?.vendor_details?.vendor_id || "",
    );
    formHook.setValue(
      "primary_purchase_info.vendor_details.branch_name",
      previousPOData?.primary_purchase_info?.vendor_details?.branch_name || "",
    );
    formHook.setValue(
      "primary_purchase_info.vendor_details.branch_id",
      previousPOData?.primary_purchase_info?.vendor_details?.branch_id || "",
    );

    formHook.setValue(
      "primary_purchase_info.clinic_details.bill_to_clinic_name",
      previousPOData?.primary_purchase_info?.clinic_details?.bill_to_clinic_name || "",
    );
    formHook.setValue(
      "primary_purchase_info.clinic_details.bill_to_clinic_id",
      previousPOData?.primary_purchase_info?.clinic_details?.bill_to_clinic_id || "",
    );
    formHook.setValue(
      "primary_purchase_info.clinic_details.bill_to_clinic_gstin",
      previousPOData?.primary_purchase_info?.clinic_details?.bill_to_clinic_gstin || "",
    );
    formHook.setValue(
      "primary_purchase_info.clinic_details.ship_to_clinic_name",
      previousPOData?.primary_purchase_info?.clinic_details?.ship_to_clinic_name || "",
    );
    formHook.setValue(
      "primary_purchase_info.clinic_details.ship_to_clinic_id",
      previousPOData?.primary_purchase_info?.clinic_details?.ship_to_clinic_id || "",
    );
    formHook.setValue(
      "primary_purchase_info.vms_category_details",
      previousPOData?.primary_purchase_info?.vms_category_details || null,
    );

    POProductsHook.replace(
      (previousPOData?.product_info || []).map((lineItem) => ({
        ...lineItem,
        is_grn_done: false,
      })),
    );

    formHook.setValue("additional_note", previousPOData?.additional_note || "");
    formHook.setValue("terms_conditions", previousPOData?.terms_conditions || "");

    formHook.setValue("invoice_details", existingInvoiceDetails || null);
    formHook.setValue("attachments", existingAttachments || []);
    formHook.setValue("invoice_attachment_file", existingInvoiceAttachmentFile);
    formHook.clearErrors();
  };

  const handleCopyPreviousPO = async () => {
    const enteredPONumber = previousPONumber.trim();
    if (!enteredPONumber) {
      setCopyPreviousPOError("Please enter a PO number.");
      return;
    }

    if (enteredPONumber === formHook.getValues("po_number")) {
      setCopyPreviousPOError("Please enter a different PO number.");
      return;
    }

    setIsCopyingPreviousPO(true);
    setCopyPreviousPOError("");

    const response = await fetchPOdetailsByNumber(enteredPONumber);
    setIsCopyingPreviousPO(false);

    if (!response?.status || !response?.data) {
      setCopyPreviousPOError(response?.message || "No PO details found.");
      return;
    }

    applyPreviousPODetailsToCurrentForm(response.data);
    hideCopyPreviousPOModal();
    setPreviousPONumber("");
    setCopyPreviousPOError("");
    const variantValidationErrorMessage = (response.data as any)?.variant_validation_errors
      ?.map((validationItem: { error?: string }) => validationItem?.error)
      ?.find(Boolean);
    if (variantValidationErrorMessage) {
      toast.error(variantValidationErrorMessage);
    }
    toast.success(`Copied details from ${enteredPONumber}`);
  };

  useEffect(() => {
    if (!purchaseOrderDetails) return;
    if (purchaseOrderDetails && isObject(purchaseOrderDetails)) {
      formHook.reset({
        ...purchaseOrderDetails,
          primary_purchase_info: {
            ...purchaseOrderDetails?.primary_purchase_info,
            delivery_date: purchaseOrderDetails?.primary_purchase_info?.delivery_date ?? "",
          },
        // Lines rejected in invoice verification are off the PO and never enter the form.
        product_info: (purchaseOrderDetails?.product_info ?? []).filter((line) => line?.ocr_decision !== "rejected"),
        attachments: purchaseOrderDetails?.invoice_details?.attachments,
        invoice_details: {
          ...purchaseOrderDetails?.invoice_details,
          invoice_value: purchaseOrderDetails?.invoice_details?.invoice_value ?? null,
        },
      });
      setCorrectNextPOStatus(purchaseOrderDetails?.po_status);
    }
  }, [purchaseOrderDetails]);

  useEffect(() => {
    if (params.get("po_id")) {
      fetchPOdetails(params.get("po_id"));
      dispatch(setActivePOId(params.get("po_id")));
    }
  }, [params.get("po_id")]);

  useEffect(() => {
    if (formHook.getValues("invoice_attachment_file")) {
      setAttachments();
    }
  }, [formHook.watch("invoice_attachment_file")]);

  const viewPoPdf = (url: string) => {
    window.open(url, "_blank");
  };

  return (
    isObject(purchaseOrderDetails) &&
    Object.keys(purchaseOrderDetails)?.length > 0 &&
    (!pdfView ? (
      <div className="bg-white px-4 p-4 rounded-md">
        <form className="mt-2 w-full">
          <div className="flex flex-wrap justify-between items-center gap-3 border-gray-200 pb-4 border-b">
            <h2 className="flex items-center gap-x-3 font-semibold text-sm text-[#1F1F23]">
              {purchaseOrderDetails?.po_status === "pre_draft" ? "New Purchase Order" : purchaseOrderDetails?.po_number}
              {purchaseOrderDetails?.po_status === "pre_draft" ? null : (
                <SimpleBadge
                  label={convertToReadableFormat(purchaseOrderDetails?.po_status)}
                  colorClassName={POStatusBadgeColor[purchaseOrderDetails?.po_status]}
                />
              )}
            </h2>
            <div className="flex gap-2">
              {purchaseOrderDetails?.pdf_url && (
                <button
                  type="button"
                  onClick={() => viewPoPdf(purchaseOrderDetails?.pdf_url)}
                  className="inline-flex justify-center items-center gap-1.5 h-8 px-4 rounded-lg font-medium text-xs whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[#F1F1F1] border border-gray-200 text-[#1F1F23] hover:bg-gray-200"
                >
                  View PO PDF
                </button>
              )}
              {purchaseOrderDetails?.grn_pdf_url && (
                <button
                  type="button"
                  onClick={() => viewPoPdf(purchaseOrderDetails?.grn_pdf_url)}
                  className="inline-flex justify-center items-center gap-1.5 h-8 px-4 rounded-lg font-medium text-xs whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[#F1F1F1] border border-gray-200 text-[#1F1F23] hover:bg-gray-200"
                >
                  View GRN PDF
                </button>
              )}
            </div>
          </div>
          <PrimaryPurchaseInfoFormFields
            formHook={formHook}
            onSubmit={handlePOupdate}
            onCopyPreviousPOClick={() => {
              setPreviousPONumber("");
              setCopyPreviousPOError("");
              openCopyPreviousPOModal();
            }}
          />
          <ManageInvoiceFormFields
            onSubmit={onSubmit}
            formHook={formHook}
            attachementFieldHook={attachementFieldHook}
            isUploading={isInvoiceUploading}
            setIsUploading={setIsInvoiceUploading}
          />
          <ProductInfoFormFields
            POProductsHook={POProductsHook}
            formHook={formHook}
            ocrGateRef={ocrGateRef}
            productCalculationRef={productCalculationRef}
            onInvoiceVerificationChange={saveInvoiceVerification}
          />
          <Tooltip id="user_detail_name" className="z-50" />
          <div className={`${POProductsHook?.fields?.length > 0 ? "mt-44" : "mt-5"}`}>
            <AdditionalNotesFormFields formHook={formHook} />
          </div>
          <div className="bottom-0 z-40 sticky flex justify-end items-center bg-white mt-10 pt-3 border-t border-gray-100">
            <POSubmitButton
              POProductsHook={POProductsHook}
              onSubmit={onSubmit}
              attachementFieldHook={attachementFieldHook}
              setPDFView={setPDFView}
              formHook={formHook}
              ocrGateRef={ocrGateRef}
              isInvoiceUploading={isInvoiceUploading}
            />
          </div>
          <ModalPrompt
            modalId="PO_COPY_PREVIOUS_MODAL"
            title="Copy Previous PO"
            dialogClassName="sm:my-8 sm:w-full sm:max-w-md sm:p-6 w-full"
            button={{
              primaryButtonText: isCopyingPreviousPO ? "Copying..." : "Copy",
              primaryButtonClassname: "bg-black text-white",
              primaryButtonDisabled: isCopyingPreviousPO,
              secondaryButtonText: "Cancel",
              secondaryCallback: closeCopyPreviousPOModal,
              primaryCallback: handleCopyPreviousPO,
              shouldHideOnClick: false,
              onCrossClick: closeCopyPreviousPOModal,
            }}
            content={
              <div>
                <p className="mt-1 text-gray-500 text-xs">
                  Enter a PO number to copy vendor, clinic and line items into this new PO.
                </p>
                <input
                  type="text"
                  value={previousPONumber}
                  onChange={(e) => {
                    setPreviousPONumber(e.target.value);
                    if (copyPreviousPOError) {
                      setCopyPreviousPOError("");
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleCopyPreviousPO();
                    }
                  }}
                  placeholder="Enter PO number"
                  className="mt-4 border border-gray-300 focus:border-blue-500 rounded-lg w-full h-9 px-3 text-xs outline-none"
                />
                {copyPreviousPOError && <p className="mt-2 text-red-500 text-xs">{copyPreviousPOError}</p>}
              </div>
            }
          />
        </form>
      </div>
    ) : (
      <POPDFPreview
        attachementFieldHook={attachementFieldHook}
        POProductsHook={POProductsHook}
        onSubmit={onSubmit}
        formData={formHook.getValues()}
        setPDFView={setPDFView}
      />
    ))
  );
};
export default OrderPurchaseAddComponent;
